/**
 * Drives every cart-page upsell card (see clvry-cart-upsell.liquid for
 * why this exists as a cart-page add-on rather than real checkout
 * customization — this store isn't on Shopify Plus). Plain fetch to
 * /cart/add.js, same pattern as clvry-donation.js.
 *
 * One or more cards can be on the page at once (2026-09-24 — the
 * section moved from a single fixed product to repeatable blocks), so
 * this initializes each [data-clvry-upsell] root independently: picking
 * a color on the Cap card never touches the Polo card's own state, and
 * each has its own /cart/add.js call so adding one doesn't require or
 * reset the others.
 */
(function () {
  function initCard(root) {
    if (root.dataset.clvryUpsellInit) return;
    root.dataset.clvryUpsellInit = "true";

    var pillGroups = root.querySelectorAll("[data-clvry-upsell-option]");
    var addBtn = root.querySelector(".clvry-upsell__add");
    var status = root.querySelector(".clvry-upsell__status");
    var image = root.querySelector("[data-clvry-upsell-image]");
    var variantsData = JSON.parse(root.querySelector("[data-clvry-upsell-variants]").textContent);

    // One slot per option position (variant.options is already in that
    // same positional order — Shopify's own Liquid/REST convention).
    // Options with only one real value never got a pill group rendered
    // (see the `option.values.size > 1` check in the liquid), so they're
    // pre-filled here from whichever variant carries that single value —
    // nothing left for the shopper to pick for those slots.
    var optionCount = variantsData.length ? variantsData[0].options.length : 0;
    var selected = new Array(optionCount).fill(null);
    for (var i = 0; i < optionCount; i++) {
      var hasPillGroup = root.querySelector('[data-clvry-upsell-option="' + i + '"]');
      if (!hasPillGroup) selected[i] = variantsData[0].options[i];
    }

    // The button reads "Add — £xx" once everything is chosen, and tells the
    // shopper what is still missing ("Choose a size") until then, so a greyed
    // button never looks broken.
    var addLabel = addBtn.textContent;
    var optionNames = [];
    try { optionNames = JSON.parse(root.dataset.optionNames || "[]"); } catch (e) {}
    function missingLabel() {
      var idx = selected.findIndex(function (v) { return v === null; });
      var name = (optionNames[idx] || "").toLowerCase();
      if (name === "size") return "Choose a size";
      if (name === "color" || name === "colour") return "Choose a colour";
      return "Choose an option";
    }

    function updateAddState() {
      var complete = selected.every(function (v) { return v !== null; });
      var variant = complete ? findVariant() : null;
      addBtn.disabled = !(complete && variant && variant.available);
      addBtn.textContent = complete ? addLabel : missingLabel();
      if (complete && (!variant || !variant.available)) {
        showStatus("That combination is out of stock — try another option.", true);
      } else {
        status.classList.add("clvry-upsell-hidden");
      }
    }

    // Swaps the photo to whatever the shopper has picked SO FAR, not
    // just once every option is chosen — e.g. clicking "Navy" alone
    // (before a size is picked) should already show the navy polo.
    // Every variant sharing that color also shares the same photo (real
    // Shopify data, checked directly), so the first partial match is
    // always the right image regardless of which other slots are still
    // unset. Falls back to the card's original default image once
    // nothing is selected at all (e.g. after the pills reset).
    function updateImage() {
      if (!image) return;
      var anySelected = selected.some(function (v) { return v !== null; });
      if (!anySelected) return;
      var match = partialMatch();
      if (match && match.image) image.src = match.image;
    }

    function partialMatch() {
      return variantsData.filter(function (v) {
        return v.options.every(function (opt, i) { return selected[i] === null || opt === selected[i]; });
      })[0];
    }

    pillGroups.forEach(function (group) {
      var index = Number(group.dataset.clvryUpsellOption);
      var pills = group.querySelectorAll(".clvry-upsell__pill");
      pills.forEach(function (pill) {
        pill.addEventListener("click", function () {
          pills.forEach(function (p) { p.classList.toggle("is-active", p === pill); });
          selected[index] = pill.dataset.value;
          updateAddState();
          updateImage();
        });
      });
    });

    function findVariant() {
      return variantsData.filter(function (v) {
        return v.options.every(function (opt, i) { return opt === selected[i]; });
      })[0];
    }

    function showStatus(message, isError) {
      status.textContent = message;
      status.classList.remove("clvry-upsell-hidden");
      status.classList.toggle("is-error", !!isError);
    }

    // The order system (worker/src/shopify.js) needs the hidden _Garment /
    // _Color / _Size properties on every printed item — the product page's
    // own form adds them (clvry-logo-only-cart-properties.js), but this
    // plain /cart/add.js call used to send none, so an upsold Cap was paid
    // for and never ordered (found by test order #1010, 2026-10-07). The
    // worker also recognises such items by title now as a backstop.
    function orderProperties(variant) {
      var garment = root.dataset.garment;
      if (!garment) return {};
      var names = [];
      try { names = JSON.parse(root.dataset.optionNames || "[]"); } catch (e) {}
      var byName = {};
      names.forEach(function (n, i) { byName[n] = variant.options[i]; });
      return { _Garment: garment, _Color: byName["Color"] || "", _Size: byName["Size"] || "" };
    }

    addBtn.addEventListener("click", function () {
      var variant = findVariant();
      if (!variant) {
        showStatus("Please pick an option first.", true);
        return;
      }

      addBtn.disabled = true;
      showStatus("Adding...", false);

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: variant.id, quantity: 1, properties: orderProperties(variant) }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Add to cart failed");
          return res.json();
        })
        .then(function () {
          showStatus("Added to your cart — reloading...", false);
          window.location.reload();
        })
        .catch(function () {
          showStatus("Something went wrong adding that — please try again.", true);
          addBtn.disabled = false;
        });
    });

    // Start each card on a colour other than black (picked at random, so
    // the row looks different from visit to visit) — every product's
    // default photo is the black one, which made the whole "Add to your
    // order" row read as three black garments. Clicking the real pill
    // reuses all the normal selection/image/Add-button logic. Cards whose
    // only colour is black keep it; products with no Color option (the
    // Blackout/Whiteout editions) are left alone.
    function startOnVariedColour() {
      var names = [];
      try { names = JSON.parse(root.dataset.optionNames || "[]"); } catch (e) {}
      var colorIndex = names.indexOf("Color");
      if (colorIndex < 0) return;
      var group = root.querySelector('[data-clvry-upsell-option="' + colorIndex + '"]');
      if (!group) return;
      var pills = Array.prototype.slice.call(group.querySelectorAll(".clvry-upsell__pill"));
      var notBlack = pills.filter(function (p) { return !/^black$/i.test(p.dataset.value); });
      var pool = notBlack.length ? notBlack : pills;
      if (pool.length) pool[Math.floor(Math.random() * pool.length)].click();
    }

    updateAddState();
    startOnVariedColour();
  }

  function init() {
    var roots = document.querySelectorAll("[data-clvry-upsell]");
    roots.forEach(initCard);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
